import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, TextField, Button, CircularProgress, InputAdornment, IconButton, Alert
} from '@mui/material';
import {
  Email, Lock, Visibility, VisibilityOff, StorefrontRounded, CheckCircleRounded
} from '@mui/icons-material';
import api from '../api';
import { saveSession } from '../auth';
import { BRAND } from '../theme';

const FEATURES = [
  'Templates, categories & products',
  'Orders and Razorpay payments',
  'Customers, FAQs & policies',
];

const Logo = ({ size = 40 }) => (
  <Box sx={{
    width: size, height: size, borderRadius: size / 3.5,
    background: BRAND.gradient, boxShadow: BRAND.glow,
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  }}>
    <StorefrontRounded sx={{ color: '#fff', fontSize: size * 0.55 }} />
  </Box>
);

const Login = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      // The api interceptor resolves error responses instead of throwing,
      // so failures arrive here as { success: false, message }.
      const res = await api.post('/api/admins/login', form);
      const body = res?.data;
      if (!body?.success || !body.data?.token) {
        setError(body?.message || 'Could not reach the server. Please try again.');
        return;
      }
      const { admin } = body.data;
      if (admin?.role !== 'admin' && admin?.role !== 'superadmin') {
        setError('Access denied: this account is not an admin.');
        return;
      }
      saveSession(body.data);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', bgcolor: 'background.default' }}>
      {/* Hero panel */}
      <Box sx={{
        display: { xs: 'none', md: 'flex' },
        flex: 1.1,
        flexDirection: 'column',
        justifyContent: 'space-between',
        position: 'relative',
        overflow: 'hidden',
        background: BRAND.heroGradient,
        color: '#fff',
        px: { md: 7, lg: 10 },
        py: 6,
      }}>
        {/* Decorative blurred orbs */}
        <Box sx={{
          position: 'absolute', width: 420, height: 420, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(123,63,242,0.55), transparent 70%)',
          top: -120, right: -100, filter: 'blur(10px)',
        }} />
        <Box sx={{
          position: 'absolute', width: 360, height: 360, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(61,123,255,0.45), transparent 70%)',
          bottom: -140, left: -80, filter: 'blur(10px)',
        }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, position: 'relative' }}>
          <Logo size={42} />
          <Typography sx={{ fontWeight: 700, fontSize: 18 }}>Salesmart</Typography>
        </Box>

        <Box sx={{ position: 'relative', maxWidth: 520 }}>
          <Typography sx={{
            fontSize: 13, fontWeight: 600, letterSpacing: 2, textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.6)', mb: 2,
          }}>
            Admin dashboard
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 800, lineHeight: 1.15, mb: 2.5 }}>
            Make it possible.{' '}
            <Box component="span" sx={{
              background: 'linear-gradient(90deg,#b99bff,#7fb0ff)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>
              Run every store from one place.
            </Box>
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: 15.5, mb: 4 }}>
            Build templates, publish products and track every order with a clean, fast admin.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {FEATURES.map(f => (
              <Box key={f} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CheckCircleRounded sx={{ fontSize: 20, color: '#9fb8ff' }} />
                <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: 14.5 }}>{f}</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 12.5, position: 'relative' }}>
          © {new Date().getFullYear()} Anainfo
        </Typography>
      </Box>

      {/* Form panel */}
      <Box sx={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: { xs: 2, sm: 4, md: 6 },
        py: 6,
      }}>
        <Box sx={{ width: '100%', maxWidth: 420 }}>
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1.5, mb: 4 }}>
            <Logo />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Salesmart</Typography>
          </Box>

          <Typography variant="h4" sx={{ mb: 1 }}>Welcome back</Typography>
          <Typography sx={{ color: 'text.secondary', mb: 4, fontSize: 15 }}>
            Sign in to continue to your dashboard.
          </Typography>

          <Paper elevation={1} sx={{ p: { xs: 3, sm: 4 }, borderRadius: '20px' }}>
            <form onSubmit={handleSubmit}>
              <Typography component="label" htmlFor="email" sx={{ fontSize: 13, fontWeight: 600, display: 'block', mb: 0.75 }}>
                Email address
              </Typography>
              <TextField
                id="email"
                name="email"
                type="email"
                placeholder="you@company.com"
                autoComplete="email"
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                fullWidth required
                sx={{ mb: 2.5 }}
                slotProps={{
                  input: {
                    startAdornment: <InputAdornment position="start"><Email sx={{ color: 'text.disabled', fontSize: 20 }} /></InputAdornment>,
                  },
                }}
              />
              <Typography component="label" htmlFor="password" sx={{ fontSize: 13, fontWeight: 600, display: 'block', mb: 0.75 }}>
                Password
              </Typography>
              <TextField
                id="password"
                name="password"
                type={showPass ? 'text' : 'password'}
                placeholder="••••••••"
                autoComplete="current-password"
                value={form.password}
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                fullWidth required
                sx={{ mb: 3 }}
                slotProps={{
                  input: {
                    startAdornment: <InputAdornment position="start"><Lock sx={{ color: 'text.disabled', fontSize: 20 }} /></InputAdornment>,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setShowPass(s => !s)}
                          edge="end"
                          aria-label={showPass ? 'Hide password' : 'Show password'}
                        >
                          {showPass ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />

              {error && <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>{error}</Alert>}

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading}
                sx={{ py: 1.5, fontSize: 15, boxShadow: BRAND.glow }}
              >
                {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Sign in'}
              </Button>
            </form>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
